import React from 'react';
import { createRoot, Root } from 'react-dom/client';

import {
  AskBeforeClosing,
  CloseModal,
  OnCloseParams
} from 'common/module/ui/lazy-modal/types';
import noop from 'common/tools/noop';

type Anchor = Element | DocumentFragment;

const routes = new WeakMap<Anchor, Root>();

const getRoute = (anchor: Anchor) => {
  if (!routes.has(anchor)) {
    routes.set(anchor, createRoot(anchor));
  }
  return routes.get(anchor);
};

/**
 * Build a LazyModal compatible module which will use ReactDOM to render the modal.
 *
 * @param {string|ModalNames} name The name of the module, should be unique across all module known by LazyModal
 * @param {function} buildComponent A function to build the React component to render (usually a ReactModal instance)
 * @param {function} onClose A function called before the module is being removed (i.e. the modal is closed)
 * @param {function} onOpen A function called when the module is being displayed (i.e. the modal is opened)
 * @return {Object} A module exploitable by LazyModal
 */
const buildReactDomModalModule = <T>(
  name: string,
  buildComponent: (
    params: T,
    closeModal: CloseModal,
    askBeforeClosing: AskBeforeClosing,
    onCloseParams: OnCloseParams
  ) => React.ReactElement,
  onClose = noop,
  onOpen = noop
) => ({
  name,
  render: (
    anchor: Anchor | null,
    params: T,
    closeModal: CloseModal,
    askBeforeClosing: AskBeforeClosing,
    onCloseParams: OnCloseParams
  ) => {
    if (anchor) {
      const root = getRoute(anchor);
      const Component = buildComponent(
        params,
        closeModal,
        askBeforeClosing,
        onCloseParams
      );
      root?.render(Component);
    }
  },
  remove: (anchor: Anchor) => {
    const root = getRoute(anchor);
    root?.unmount();
    routes.delete(anchor);
  },
  onClose: onClose,
  onOpen: onOpen
});

export default buildReactDomModalModule;
